import React, { useEffect, useRef } from 'react';
import { GoArrowUpRight, GoArrowRight } from "react-icons/go";
import { gsap } from 'gsap';
import { floatLoop, brutalistBtn, magneticHover } from '../../utils/animationUtils';

const TICKER_ITEMS = [
    'FULLSTACK DEV', 'EAST JAVA', 'NEXT.JS',
    'LARAVEL', 'REACT', 'VPS & SERVER',
    'TYPESCRIPT', 'NODE.JS', '99.9% UPTIME',
];

const HeroSection = () => {
    const containerRef  = useRef(null);
    const line1Ref      = useRef(null);
    const line2Ref      = useRef(null);
    const line3Ref      = useRef(null);
    const shape1Ref     = useRef(null);
    const stampRef      = useRef(null);
    const stampTextRef  = useRef(null);
    const tickerRef     = useRef(null);

    useEffect(() => {
        const ctx = gsap.context(() => {
            gsap.set(['.hero-badge', '.hero-greeting', '.hero-subtitle', '.hero-btn', '.hero-photo-card', '.hero-stat'], { opacity: 0 });
            gsap.set([line1Ref.current, line2Ref.current, line3Ref.current], { opacity: 0, y: 80, rotationX: -30 });

            const tl = gsap.timeline({ delay: 0.2 });
            tl.to('.hero-badge',      { opacity: 1, scale: 1, ease: 'elastic.out(1,0.5)', duration: 0.7, from: { scale: 0 } })
              .to('.hero-greeting',   { y: 0, opacity: 1, duration: 0.5, ease: 'power3.out' }, '-=0.3')
              .to([line1Ref.current, line2Ref.current, line3Ref.current], { y: 0, opacity: 1, rotationX: 0, stagger: 0.12, duration: 0.8, ease: 'power3.out' }, '-=0.2')
              .to('.hero-subtitle',   { y: 0, opacity: 1, duration: 0.6, ease: 'power3.out' }, '-=0.4')
              .to('.hero-btn',        { y: 0, opacity: 1, scale: 1, stagger: 0.1, duration: 0.5, ease: 'back.out(1.7)' }, '-=0.4')
              .to('.hero-photo-card', { x: 0, opacity: 1, rotation: -2, ease: 'power4.out', duration: 1 }, '-=0.6')
              .to('.hero-stat',       { y: 0, opacity: 1, stagger: 0.1, duration: 0.5, ease: 'power3.out' }, '-=0.8');

            floatLoop(shape1Ref.current, -12, 3.5);
            gsap.to(shape1Ref.current, { rotation: 360, repeat: -1, duration: 10, ease: 'none' });

            if (stampRef.current && stampTextRef.current) {
                floatLoop(stampRef.current, 8, 4);
                gsap.to(stampTextRef.current, { rotation: 360, duration: 12, ease: 'none', repeat: -1, transformOrigin: 'center center' });
            }

            // Ticker — seamless loop
            if (tickerRef.current) {
                const totalWidth = tickerRef.current.scrollWidth / 2;
                gsap.to(tickerRef.current, {
                    x: -totalWidth,
                    duration: 22,
                    ease: 'none',
                    repeat: -1,
                });
            }

            const onMouseMove = (e) => {
                const x = (e.clientX / window.innerWidth - 0.5) * 80;
                const y = (e.clientY / window.innerHeight - 0.5) * 80;
                gsap.to(shape1Ref.current, { x: x * 0.4, y: y * 0.4, duration: 1, ease: 'power2.out' });
            };
            window.addEventListener('mousemove', onMouseMove);
            brutalistBtn('.hero-btn');
            magneticHover('.hero-btn');
            return () => { window.removeEventListener('mousemove', onMouseMove); };
        }, containerRef);

        return () => ctx.revert();
    }, []);

    return (
        <section
            id="home"
            ref={containerRef}
            className="relative overflow-hidden"
            style={{
                height: '100svh',
                minHeight: 620,
                display: 'flex',
                alignItems: 'stretch',
                // Extra bottom padding = ticker height (40px)
                paddingTop: 'clamp(64px, 7vh, 88px)',
                paddingBottom: 'clamp(52px, 7vh, 72px)',
                paddingLeft: 'var(--section-px)',
                paddingRight: 'var(--section-px)',
                boxSizing: 'border-box',
            }}
        >
            {/* Stamp — positioned absolute top right */}
            <div
                ref={stampRef}
                className="hero-stat pointer-events-none select-none"
                style={{
                    position: 'absolute',
                    top: 'clamp(80px, 12vh, 140px)',
                    right: 'clamp(40px, 8vw, 120px)',
                    width: 'clamp(80px, 10vh, 120px)',
                    height: 'clamp(80px, 10vh, 120px)',
                    zIndex: 20,
                }}
            >
                <div style={{ position: 'relative', width: '100%', height: '100%', border: '2.5px solid #111', borderRadius: '50%', background: '#FFE600', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '3px 3px 0 #111' }}>
                    <svg ref={stampTextRef} viewBox="0 0 140 140" style={{ position: 'absolute', width: '100%', height: '100%', top: 0, left: 0 }}>
                        <defs>
                            <path id="stamp-circle" d="M 70,70 m -52,0 a 52,52 0 1,1 104,0 a 52,52 0 1,1 -104,0" fill="none" />
                        </defs>
                        <text style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, fontWeight: 700, letterSpacing: '3px', fill: '#111' }}>
                            <textPath href="#stamp-circle" startOffset="0%">FULLSTACK · 2025 · AHMDHZQ ·&nbsp;</textPath>
                        </text>
                    </svg>
                    <span style={{ fontFamily: "'Syne', sans-serif", fontWeight: 800, fontSize: 'clamp(14px, 1.4vw, 20px)', color: '#111', position: 'relative', zIndex: 1 }}>AH</span>
                </div>
            </div>

            {/* Decorative star */}
            <div
                ref={shape1Ref}
                className="absolute z-0 pointer-events-none select-none text-[#FFE600]"
                style={{ fontSize: 24, lineHeight: 1, top: 'clamp(200px, 25vh, 280px)', right: 'clamp(60px, 10vw, 160px)', zIndex: 0 }}
            >★</div>

            {/* ── RUNNING TEXT TICKER — absolute at very bottom of section ── */}
            <div
                style={{
                    position: 'absolute',
                    bottom: 0, left: 0, right: 0,
                    height: 40,
                    backgroundColor: '#111',
                    borderTop: '2.5px solid #111',
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    zIndex: 10,
                }}
            >
                {/* Two copies for seamless loop */}
                <div ref={tickerRef} style={{ display: 'flex', width: 'max-content', flexShrink: 0 }}>
                    {[0, 1].map(copy => (
                        <div key={copy} style={{ display: 'flex', alignItems: 'center' }}>
                            {TICKER_ITEMS.map((item, i) => (
                                <span
                                    key={i}
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 14,
                                        padding: '0 20px',
                                        fontFamily: "'Space Mono', monospace",
                                        fontSize: 11,
                                        fontWeight: 700,
                                        letterSpacing: '0.14em',
                                        textTransform: 'uppercase',
                                        color: '#FFE600',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {item}
                                    <span style={{ fontSize: 7, color: 'rgba(255,230,0,0.5)', flexShrink: 0 }}>◆</span>
                                </span>
                            ))}
                        </div>
                    ))}
                </div>
            </div>

            {/* Main layout */}
            <div className="hero-container">

                {/* LEFT — space-between top vs bottom */}
                <div className="hero-left">

                    {/* TOP BLOCK */}
                    <div>
                        <p
                            className="hero-greeting"
                            style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 'var(--text-lg)', fontWeight: 500, color: 'var(--ink-muted)', lineHeight: 1.6, marginBottom: 'clamp(3px, 0.5vh, 8px)' }}
                        >
                            Hi, I'm Ahmad Haziq
                        </p>

                        <div
                            className="hero-badge inline-flex items-center gap-2 bg-[#FFE600] border-2 border-[#111] rounded-full text-[#111]"
                            style={{ fontFamily: "'Space Mono', monospace", fontWeight: 700, fontSize: 'var(--text-badge)', letterSpacing: '0.18em', textTransform: 'uppercase', boxShadow: '3px 3px 0 #111', padding: 'clamp(4px, 0.6vh, 8px) clamp(12px, 1.5vw, 16px)', marginBottom: 'clamp(6px, 1vh, 14px)', display: 'inline-flex' }}
                        >
                            <span className="w-2 h-2 rounded-full bg-[#111] inline-block" />
                            FULLSTACK DEV
                        </div>

                        <h1 style={{ perspective: 400, fontFamily: "'Syne', sans-serif", fontWeight: 800, letterSpacing: '-0.04em', lineHeight: 1.0, marginBottom: 'clamp(6px, 1.2vh, 16px)' }}>
                            <span ref={line1Ref} style={{ display: 'block', fontSize: 'var(--text-hero)', color: 'var(--ink)' }}>Engineering</span>
                            <span ref={line2Ref} style={{ display: 'block', fontSize: 'var(--text-hero)', color: 'var(--ink)' }}>
                                High-<span style={{ color: '#FFE600' }}>Impact</span>
                            </span>
                            <span ref={line3Ref} style={{ display: 'block', fontSize: 'var(--text-hero)', color: 'var(--ink)' }}>Solutions.</span>
                        </h1>

                        <p
                            className="hero-subtitle"
                            style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 'var(--text-base)', fontWeight: 400, lineHeight: 1.65, color: 'var(--ink-muted)', maxWidth: '48ch', marginBottom: 'clamp(8px, 1.8vh, 20px)' }}
                        >
                            Full-Stack Developer based in{' '}
                            <strong style={{ color: 'var(--ink)', fontWeight: 600 }}>East Java</strong>.{' '}
                            I architect scalable web systems — from frontend to server infrastructure.
                        </p>

                        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                            <button
                                className="hero-btn inline-flex items-center gap-2 bg-[#FFE600] text-[#111] border-2 border-[#111] rounded-lg"
                                style={{ fontFamily: "'Syne', sans-serif", fontWeight: 800, fontSize: 'var(--text-sm)', letterSpacing: '0.04em', boxShadow: '5px 5px 0 #111', padding: 'clamp(10px, 1.4vh, 16px) clamp(18px, 2vw, 28px)' }}
                            >
                                Hire Me <GoArrowUpRight size={18} strokeWidth={1} />
                            </button>
                            <button
                                className="hero-btn inline-flex items-center gap-2 bg-white text-[#111] border-2 border-[#111] rounded-lg"
                                style={{ fontFamily: "'Syne', sans-serif", fontWeight: 800, fontSize: 'var(--text-sm)', letterSpacing: '0.04em', boxShadow: '5px 5px 0 #111', padding: 'clamp(10px, 1.4vh, 16px) clamp(18px, 2vw, 28px)' }}
                            >
                                My Work <GoArrowRight size={18} />
                            </button>
                        </div>
                    </div>

                    {/* BOTTOM BLOCK — stat cards only (stamp moved to right) */}
                    <div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            {[
                                { val: '6+',  label: 'Projects', bg: '#FFFFFF', textColor: '#111', labelColor: '#666' },
                                { val: '12+', label: 'Tech Stack', bg: '#FF3366', textColor: '#FFF', labelColor: 'rgba(255,255,255,0.9)' },
                                { val: '99%', label: 'UAT Pass', bg: '#00E5FF', textColor: '#111', labelColor: '#444' },
                            ].map(s => (
                                <div
                                    key={s.label}
                                    className="hero-stat border-2 border-[#111] rounded-xl px-4 py-3 flex flex-col justify-center"
                                    style={{ backgroundColor: s.bg, boxShadow: '4px 4px 0 #111' }}
                                >
                                    <p style={{ fontFamily: "'Syne', sans-serif", fontSize: 'clamp(20px, 2.5vw, 32px)', fontWeight: 800, letterSpacing: '-0.03em', color: s.textColor, lineHeight: 1 }}>{s.val}</p>
                                    <p style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: s.labelColor, marginTop: 4 }}>{s.label}</p>
                                </div>
                            ))}
                            {/* Years Experience Card */}
                            <div
                                className="hero-stat border-2 border-[#111] rounded-xl px-4 py-3 flex flex-col justify-center"
                                style={{ backgroundColor: '#AAFF00', boxShadow: '4px 4px 0 #111' }}
                            >
                                <p style={{ fontFamily: "'Syne', sans-serif", fontSize: 'clamp(20px, 2.5vw, 32px)', fontWeight: 800, letterSpacing: '-0.03em', color: '#111', lineHeight: 1 }}>3+</p>
                                <p style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#444', marginTop: 4 }}>Years Exp</p>
                            </div>
                        </div>
                    </div>

                </div>

                {/* RIGHT: Photo + stamp overlay */}
                <div className="hero-right" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>



                    <div className="hero-photo-card relative" style={{ transform: 'translateX(80px)', opacity: 0 }}>
                        <div
                            className="relative"
                            style={{
                                width: 'clamp(190px, 22vw, 290px)',
                                height: 'clamp(250px, 48vh, 420px)',
                                borderRadius: 12,
                                transform: 'rotate(-2deg)',
                                overflow: 'visible',
                            }}
                        >
                            <div
                                style={{
                                    position: 'absolute',
                                    width: '105%',
                                    height: '85%',
                                    background: '#FFE600',
                                    border: '3px solid #111',
                                    boxShadow: '16px 16px 0 #111',
                                    borderRadius: 12,
                                    bottom: -16,
                                    right: -16,
                                    zIndex: 0,
                                }}
                            />
                            <img
                                src="/assets/profile.png"
                                alt="Ahmad Haziq"
                                style={{ position: 'relative', zIndex: 1, width: '100%', height: '100%', objectFit: 'cover', borderRadius: 12, display: 'block' }}
                            />
                        </div>
                    </div>
                </div>

            </div>
        </section>
    );
};

export default HeroSection;